/**
 * Creates an OncoEMR task via the backend (sync OncoEMR + Postgres persist).
 * @module createOncoemrTask
 */

import {
  CreateOncoemrTaskRequest,
  CreateOncoemrTaskResponse,
} from "../../data-model/oncoemrTasksModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { formatErrorDetail } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * Creates a new OncoEMR task and persists fanout rows for recipients.
 *
 * @param body - Org, user, MRN, title, initial message, and recipient OncoEMR user ids.
 * @returns Creator row id, OncoEMR task id, and fanout rows.
 * @throws Error when the HTTP response is not 201 or the body cannot be parsed.
 */
export const createOncoemrTask = async (
  body: CreateOncoemrTaskRequest,
): Promise<CreateOncoemrTaskResponse> => {
  const bearerToken = await getBearerToken();

  const response = await fetch(API_ENDPOINTS.ONCOEMR_TASKS, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const message = formatErrorDetail(errorData, response.statusText);
    throw new Error(
      `Failed to create OncoEMR task (${response.status}): ${message}`,
    );
  }

  return (await response.json()) as CreateOncoemrTaskResponse;
};
